import { getAuth, onAuthStateChanged, OAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { child, equalTo, get, getDatabase, onValue, orderByChild, push, query, ref, remove, set, update } from "firebase/database";
import { initializeApp } from "firebase/app"

import Utils from "./Utils";
import { ClientError } from './Types.js';
import appConfig from './AppConfig';

const firebaseConfig = {
    apiKey: "AIzaSyBNdFL0tGR2GAs2x5VzIUPMOuUcn5OqHuo",
    authDomain: "jotsyncapp.firebaseapp.com",
    databaseURL: "https://jotsyncapp-default-rtdb.firebaseio.com",
    projectId: "jotsyncapp",
    storageBucket: "jotsyncapp.appspot.com",
    messagingSenderId: "284576005398",
    appId: "1:284576005398:web:fcea31a9d7b745a3ca2437",
    measurementId: "G-ECR8BG748C"
};

const firebaseApp = initializeApp(firebaseConfig);

class DataManager {
    constructor() {
        this.auth = getAuth(firebaseApp);
        this.database = getDatabase(firebaseApp)
        this.recordRef = null;
        this.user = null;
        this.setting = null;
    }

    async getVip(user) {
        await user.getIdToken(true);
        const idTokenResult = await user.getIdTokenResult();
        return idTokenResult.claims.vip || 0;
    }

    async _setUser(user) {
        this.user = user;
        if (user) {
            this.recordRef = ref(this.database, `users/${user.uid}/record`);
            const vip = await this.getVip(user);
            this.setting = appConfig.getSetting(vip);
        } else {
            this.recordRef = null;
            this.setting = null;
        }
    }

    getSetting() {
      return this.setting;
    }
  
    signInWithApple(callback) {
        const provider = new OAuthProvider('apple.com');
        signInWithPopup(this.auth, provider)
            .then(() => {
                callback();
            })
            .catch((error) => {
                callback(error);
            });
    }

    async signOut() {
        return signOut(this.auth);
    }

    onAuthStateChanged(callback) {
        return onAuthStateChanged(this.auth, (user) => {
            this._setUser(user).then(() => {
                if (callback) {
                    callback(user);
                }
            });
        });
    }

    addValueChangedListener(callback, errorCallback) {
        if (this.recordRef) {
            const sortedQuery = query(this.recordRef, orderByChild("updated"));
            return onValue(sortedQuery, callback, errorCallback);
        }
    }

    async createRecord(record) {
        if (this.recordRef) {
            await this._checkTotal(true);
            this._preprocessRecord(record);
            const time = Utils.formattedUtcDate();
            record.created = record.updated = time;
            const newRecordRef = push(this.recordRef);
            await set(newRecordRef, record);
            return newRecordRef.key;
        }
    }

    async getRecords() {
        if (this.recordRef) {
            return get(this.recordRef);
        }
    }

    async getRecordCount() {
        if (this.recordRef) {
            const snapshot = await get(this.recordRef);
            return snapshot.size;
        }
    }

    async _checkTotal(creating) {
        const maxItemCount = this.setting.totalItemLimit;
        console.log("total item limit", maxItemCount);
        const count = await this.getRecordCount();
        console.log("Count of records: ", count);
        const newCount = count + (creating ? 1 : 0);
        if (newCount > maxItemCount) {
            const extra = newCount - maxItemCount;
            throw new ClientError(ClientError.TOO_MANY_ITEMS,
                `The total number of items (${count}) exceeds the maximum allowed limit (${maxItemCount}). Please remove ${extra} of them before proceeding.`);
        }
    }

    async _preprocessRecord(record) {
        const title = record.title || "";
        console.log("title limit", this.setting.titleCharacterLimit);
        if (title.length > this.setting.titleCharacterLimit) {
            record.title = title.substring(0, this.setting.titleCharacterLimit);
        }

        let content = record.content || "";
        console.log("content limit", this.setting.contentCharacterLimit);
        if (content.length > this.setting.contentCharacterLimit) {
            content = record.content = content.substring(0, this.setting.contentCharacterLimit);
        }

        console.log("hash threshold", this.setting.hashThreshold)
        if (content.length > this.setting.hashThreshold) {
            record.hash = await Utils.sha256(content);
        } else if (record.content != null) {
            record.hash = "";
        }
    }

    async updateRecord(recordId, updates, updateTime = false) {
        if (this.recordRef) {
            await this._checkTotal(false);

            const ref = child(this.recordRef, recordId);
            if (updateTime) {
                updates.updated = Utils.formattedUtcDate();
            }
            await this._preprocessRecord(updates);
            return update(ref, updates);
        }
    }

    async moveRecord(recordId, newCategory) {
        if (this.recordRef) {
            const ref = child(this.recordRef, recordId);
            return update(ref, { category: newCategory });
        }
    }

    async pinRecord(recordId, pinned) {
        if (this.recordRef) {
            const ref = child(this.recordRef, recordId);
            return update(ref, { pinned });
        }
    }

    async deleteRecord(recordId) {
        if (this.recordRef) {
            const ref = child(this.recordRef, recordId);
            return remove(ref);
        }
    }

    async deleteRecords(key, value) {
        if (!this.recordRef) {
            return;
        }

        const delQuery = query(this.recordRef, orderByChild(key), equalTo(value));
        onValue(delQuery, (snapshot) => {
            snapshot.forEach((childSnapshot) => {
                const recordId = childSnapshot.key;
                const ref = child(this.recordRef, recordId);
                remove(ref);
            });
        });
    }

    async emptyTrash() {
        this.deleteRecords("category", "Trash");
    }

    search(keyword, onDataReceived) {
        if (!this.recordRef) {
          return;
        }
      
        const handleSnapshot = (snapshot) => {
          const results = [];
          snapshot.forEach((childSnapshot) => {
            const recordId = childSnapshot.key;
            const recordData = childSnapshot.val();
            if (
              recordData.title.toLowerCase().includes(keyword) ||
              (recordData.type === "Text" &&
                recordData.content.toLowerCase().includes(keyword))
            ) {
              results.push({ id: recordId, data: recordData });
            }
          });
          return results;
        };
      
        const callback = (snapshot) => {
          const results = handleSnapshot(snapshot);
          onDataReceived(results);
        };
        
        const unsubscribe = onValue(this.recordRef, callback);
        return () => { unsubscribe(); };
      }
}

const dataManager = new DataManager();
export default dataManager;
